import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/DespesasStatus_CarregaStatusInput.ts';
import '../contracts/DespesasStatus_CreateInput.ts';
import '../contracts/DespesasStatus_UpdateInput.ts';
import '../contracts/DespesasStatus_DeleteInput.ts';

import '../contracts/DespesasStatus_CarregaStatusResponse';
import '../contracts/DespesasStatus_CarregaStatusSingleResponse';
import '../contracts/DespesasStatus_CreateResponse';
import '../contracts/DespesasStatus_DeleteResponse';
import '../contracts/DespesasStatus_UpdateResponse';
import type { DespesasStatus_CreateInput } from '../contracts/DespesasStatus_CreateInput';
import type { DespesasStatus_DeleteInput } from '../contracts/DespesasStatus_DeleteInput';
import type { DespesasStatus_UpdateInput } from '../contracts/DespesasStatus_UpdateInput';
/**
 * Composable for managing expense statuses (DespesasStatus)
 *
 * Provides methods for CRUD operations on expense statuses
 */
export function useDespesasStatus() {
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Fetch all expense statuses
   * @returns {Promise<Array>} Array of status objects
   */
  const fetchStatus = async () => {
    log('useDespesasStatus', 'fetchStatus', 'Fetching expense statuses');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post('/index.php/config/despesas_status/carrega_status', null, { inputContract: 'DespesasStatus_CarregaStatusInput', responseContract: 'DespesasStatus_CarregaStatusResponse' });

      if (response.success) {
        log('useDespesasStatus', 'fetchStatus', 'Statuses loaded successfully', response.dados);
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Erro ao carregar status');
      }
    } catch (err) {
      log('useDespesasStatus', 'fetchStatus', 'Error fetching statuses', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch a single expense status by ID
   * @param {number} id - Status ID
   * @returns {Promise<Object>} Status object
   */
  const fetchStatusById = async (id: any) => {
    log('useDespesasStatus', 'fetchStatusById', 'Fetching expense status', { id });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/config/despesas_status/carrega_status_single/${id}`, { responseContract: 'DespesasStatus_CarregaStatusSingleResponse' });

      if (response.success) {
        log('useDespesasStatus', 'fetchStatusById', 'Status loaded successfully', response.dados);
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao carregar status');
      }
    } catch (err) {
      log('useDespesasStatus', 'fetchStatusById', 'Error fetching status', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Create a new expense status
   * @param {Object} statusData - Status data
   * @param {string} statusData.despesa_status_titulo - Status title
   * @returns {Promise<Object>} Created status with ID
   */
  const createStatus = async (statusData: DespesasStatus_CreateInput) => {
    log('useDespesasStatus', 'createStatus', 'Creating status', statusData);
    isLoading.value = true;
    error.value = null;

    try {
      validate('DespesasStatus_CreateInput', statusData);
      const response = await httpService.post('/index.php/config/despesas_status/create', statusData, { responseContract: 'DespesasStatus_CreateResponse' });

      if (response.success) {
        log('useDespesasStatus', 'createStatus', 'Status created successfully', response.dados);
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao criar status');
      }
    } catch (err) {
      log('useDespesasStatus', 'createStatus', 'Error creating status', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update an existing expense status
   * @param {number} id - Status ID
   * @param {Object} statusData - Status data to update
   * @returns {Promise<Object>} Response object
   */
  const updateStatus = async (id: any, statusData: any) => {
    log('useDespesasStatus', 'updateStatus', 'Updating status', { id, statusData });
    isLoading.value = true;
    error.value = null;

    try {
      const payload: DespesasStatus_UpdateInput = {
        despesa_status_id: id,
        ...statusData
      };

      validate('DespesasStatus_UpdateInput', payload);
      const response = await httpService.post('/index.php/config/despesas_status/update', payload, { responseContract: 'DespesasStatus_UpdateResponse' });

      if (response.success) {
        log('useDespesasStatus', 'updateStatus', 'Status updated successfully');
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao atualizar status');
      }
    } catch (err) {
      log('useDespesasStatus', 'updateStatus', 'Error updating status', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete an expense status
   * @param {number} id - Status ID
   * @returns {Promise<Object>} Response object
   */
  const deleteStatus = async (id: any) => {
    log('useDespesasStatus', 'deleteStatus', 'Deleting status', { id });
    isLoading.value = true;
    error.value = null;

    try {
      const payload: DespesasStatus_DeleteInput = { despesa_status_id: id };
      validate('DespesasStatus_DeleteInput', payload);
      const response = await httpService.post('/index.php/config/despesas_status/delete', payload, { responseContract: 'DespesasStatus_DeleteResponse' });

      if (response.success) {
        log('useDespesasStatus', 'deleteStatus', 'Status deleted successfully');
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao excluir status');
      }
    } catch (err) {
      log('useDespesasStatus', 'deleteStatus', 'Error deleting status', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    fetchStatus,
    fetchStatusById,
    createStatus,
    updateStatus,
    deleteStatus
  };
}
